import ReactGA from 'react-ga4';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';

// Initialize GA4
export const initGA = (measurementId: string) => {
  ReactGA.initialize(measurementId);
  console.log('Google Analytics initialized with ID:', measurementId);
};

// Initialize Amplitude — defaultTracking off so it only receives the same
// explicit events GA4 does, not autocaptured clicks/sessions/pageviews too.
export const initAmplitude = (apiKey: string) => {
  amplitude.init(apiKey, { defaultTracking: false });
  console.log('Amplitude initialized');
};

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

// Initialize Meta Pixel — equivalent to Meta's own bootstrap snippet, just
// written out instead of eval'ing their minified version.
export const initMetaPixel = (pixelId: string) => {
  if (window.fbq) return; // already loaded

  const fbq: any = function (...args: any[]) {
    fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  // Automatic Configuration infers its own events from every button click and
  // page interaction it can detect — the exact noise the 4 deliberate events
  // below are meant to replace. Must be set before init() to take effect.
  window.fbq('set', 'autoConfig', false, pixelId);
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  console.log('Meta Pixel initialized with ID:', pixelId);
};

// True when the currently logged-in session belongs to an admin — checked
// defensively before every Meta Pixel fire below. Nothing in AdminDashboard/
// currently imports this file at all (verified), so this shouldn't ever
// trigger today, but a stray admin action reaching one of these functions in
// the future would otherwise report a fake conversion to Meta and skew ad
// optimization/spend — worth guarding at the source rather than relying on
// "nobody happened to import analytics.ts from an admin page" staying true.
export const isAdminSession = (): boolean => {
  try {
    const stored = localStorage.getItem('user');
    const role = stored && JSON.parse(stored)?.role;
    return role === 'admin' || role === 'sales' || role === 'superadmin';
  } catch {
    return false;
  }
};

// Fires a Meta standard event — only the handful mapped to real funnel
// moments (see trackSignUp/trackBookCreated/trackCoverDesignAddonAdded/
// trackBookSubmitted below), not every product event already tracked via
// GA4/Amplitude. A small, high-signal set optimizes ad delivery better than
// a large one. No-ops safely if the pixel was never initialized (e.g.
// REACT_APP_META_PIXEL_ID unset in this environment), or if the current
// session is an admin's — admin activity is never a real conversion.
const trackMetaPixel = (eventName: string, params?: Record<string, any>) => {
  if (!window.fbq || isAdminSession()) return;
  window.fbq('track', eventName, params);
};

// Ties subsequent events to a real user instead of an anonymous device, and
// optionally sets user properties for segmentation — call once the authenticated
// user's id is known (login, or app-load rehydration). Undefined/null/empty
// values are skipped rather than set, since callers don't always have every field.
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  amplitude.setUserId(userId);
  if (!properties) return;
  const identifyObj = new AmplitudeIdentify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') identifyObj.set(key, value);
  });
  amplitude.identify(identifyObj);
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  console.log('[GA4] pageview:', { page: path, title });
  ReactGA.send({ hitType: 'pageview', page: path, title });
  amplitude.track('page_view', { page: path, title });
};

// Generic event tracking
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  console.log(`[GA4] ${eventName}:`, params);
  ReactGA.event(eventName, params);
  amplitude.track(eventName, params);
};

// ==================== AUTHENTICATION EVENTS ====================

export const trackSignUp = () => {
  console.log('[GA4] sign_up');
  ReactGA.event('sign_up', {});
  amplitude.track('sign_up', {});
  trackMetaPixel('CompleteRegistration');
};

export const trackOtpSent = () => {
  console.log('[GA4] otp_sent');
  ReactGA.event('otp_sent', {});
  amplitude.track('otp_sent', {});
};

export const trackOtpVerification = (status: 'success' | 'failed') => {
  console.log('[GA4] otp_verification:', { status });
  ReactGA.event('otp_verification', { status });
  amplitude.track('otp_verification', { status });
};

export const trackLoginStart = (method: 'google' | 'otp') => {
  console.log('[GA4] login_start:', { method });
  ReactGA.event('login_start', { method });
  amplitude.track('login_start', { method });
};

export const trackLoginSuccess = (method: 'google' | 'otp') => {
  console.log('[GA4] login_success:', { method });
  ReactGA.event('login_success', { method });
  amplitude.track('login_success', { method });
  // Not a Meta standard event — sent as a custom event so returning-user
  // sign-ins are visible in Events Manager alongside CompleteRegistration/Lead.
  trackMetaPixel('Login', { method });
};

export const trackLoginFailure = (method: 'google' | 'otp', errorReason: string) => {
  console.log('[GA4] login_failure:', { method, error_reason: errorReason });
  ReactGA.event('login_failure', { method, error_reason: errorReason });
  amplitude.track('login_failure', { method, error_reason: errorReason });
};

export const trackSessionExpired = () => {
  console.log('[GA4] session_expired');
  ReactGA.event('session_expired', {});
  amplitude.track('session_expired', {});
};

export const trackLogout = () => {
  console.log('[GA4] logout');
  ReactGA.event('logout', {});
  amplitude.track('logout', {});
};

export const trackLanded = (bookNames: string[]) => {
  console.log('[GA4] landed:', { book_names: bookNames.join(', ') });
  ReactGA.event('landed', { book_names: bookNames.join(', ') });
  amplitude.track('landed', { book_names: bookNames.join(', ') });
};

// ==================== BOOK INFO EVENTS ====================

export const trackBookInfoLanded = (pageName: string) => {
  console.log('[GA4] landed (book_info):', { page_name: pageName });
  ReactGA.event('landed', { page_name: pageName });
  amplitude.track('landed', { page_name: pageName });
};

export const trackBookInfoClicked = (language: string) => {
  console.log('[GA4] clicked (book_info):', { language });
  ReactGA.event('clicked', { language });
  amplitude.track('clicked', { language });
};

export const trackBookInfo = (params: {
  language: string;
  title: string;
  subtitle: string;
  author: string;
  contributors: string;
}) => {
  console.log('[GA4] book_info:', params);
  ReactGA.event('book_info', params);
  amplitude.track('book_info', params);
};

export const trackIsbnRequired = (required: 'yes' | 'no') => {
  console.log('[GA4] isbn_required:', { required });
  ReactGA.event('isbn_required', { required });
  amplitude.track('isbn_required', { required });
};

// ==================== BOOK FORM FACTOR EVENTS ====================

export const trackBookFormFactorLanded = (pageName: string) => {
  console.log('[GA4] landed (book_form_factor):', { page_name: pageName });
  ReactGA.event('landed', { page_name: pageName });
  amplitude.track('landed', { page_name: pageName });
};

export const trackBookFormFactorClicked = (trimSize: string) => {
  console.log('[GA4] clicked (book_form_factor):', { trim_size: trimSize });
  ReactGA.event('clicked', { trim_size: trimSize });
  amplitude.track('clicked', { trim_size: trimSize });
};

export const trackBookType = (params: {
  trim_size: string;
  binding: string;
  print_color: string;
  paper: string;
  cover_finish: string;
}) => {
  console.log('[GA4] book_type:', params);
  ReactGA.event('book_type', params);
  amplitude.track('book_type', params);
};

// ==================== UPLOAD JOURNEY EVENTS ====================

export const trackJourneySelected = (journeyType: string) => {
  console.log('[GA4] journey_selected:', { journey_type: journeyType });
  ReactGA.event('journey_selected', { journey_type: journeyType });
  amplitude.track('journey_selected', { journey_type: journeyType });
};

export const trackUploadStart = (fileType: string, journeyType: string) => {
  console.log('[GA4] upload_start:', { file_type: fileType, journey_type: journeyType });
  ReactGA.event('upload_start', { file_type: fileType, journey_type: journeyType });
  amplitude.track('upload_start', { file_type: fileType, journey_type: journeyType });
};

// isFirstRealContent: true only when this book had no uploaded file AND no
// real typed content before this specific upload — i.e. this is genuinely
// the first manuscript this book has ever had, not a Replace of an existing
// one. Meta's Lead should fire once per book, not once per upload click.
export const trackUploadComplete = (fileType: string, fileSizeMb: number, journeyType: string, isFirstRealContent: boolean) => {
  console.log('[GA4] upload_complete:', { file_type: fileType, file_size_mb: fileSizeMb, journey_type: journeyType });
  ReactGA.event('upload_complete', { file_type: fileType, file_size_mb: fileSizeMb, journey_type: journeyType });
  amplitude.track('upload_complete', { file_type: fileType, file_size_mb: fileSizeMb, journey_type: journeyType });
  // Real manuscript uploaded and its page count measured — a much stronger
  // intent signal than book creation (this is the only call site, the
  // manuscript-upload path specifically, not cover uploads). Only the first
  // time for this book — replacing an existing upload isn't a new Lead.
  if (isFirstRealContent) trackMetaPixel('Lead');
};

export const trackUploadFailed = (errorReason: string) => {
  console.log('[GA4] upload_failed:', { error_reason: errorReason });
  ReactGA.event('upload_failed', { error_reason: errorReason });
  amplitude.track('upload_failed', { error_reason: errorReason });
};

// ==================== PDF RESIZE EVENTS ====================
// "Keep my original design" path — resizes an uploaded PDF's pages to the
// book's chosen trim size without re-typesetting, as an alternative to the
// OCR-based "Convert to editable content" path.

export const trackPdfResizeInitiated = (bookSize: string) => {
  console.log('[GA4] pdf_resize_initiated:', { book_size: bookSize });
  ReactGA.event('pdf_resize_initiated', { book_size: bookSize });
  amplitude.track('pdf_resize_initiated', { book_size: bookSize });
};

export const trackPdfResizeCompleted = (params: { book_size: string; page_count: number }) => {
  console.log('[GA4] pdf_resize_completed:', params);
  ReactGA.event('pdf_resize_completed', params);
  amplitude.track('pdf_resize_completed', params);
};

export const trackPdfResizeFailed = (errorReason: string) => {
  console.log('[GA4] pdf_resize_failed:', { error_reason: errorReason });
  ReactGA.event('pdf_resize_failed', { error_reason: errorReason });
  amplitude.track('pdf_resize_failed', { error_reason: errorReason });
};

// ==================== INLINE EDITOR EVENTS ====================

export const trackEditorOpened = () => {
  console.log('[GA4] editor_opened');
  ReactGA.event('editor_opened', {});
  amplitude.track('editor_opened', {});
};

export const trackEditorReady = () => {
  console.log('[GA4] editor_ready');
  ReactGA.event('editor_ready', {});
  amplitude.track('editor_ready', {});
};

export const trackNewPage = (pageName: string, title: string) => {
  console.log('[GA4] new_page:', { page_name: pageName, title });
  ReactGA.event('new_page', { page_name: pageName, title });
  amplitude.track('new_page', { page_name: pageName, title });
};

export const trackDeletePage = (pageName: string, title: string) => {
  console.log('[GA4] delete_page:', { page_name: pageName, title });
  ReactGA.event('delete_page', { page_name: pageName, title });
  amplitude.track('delete_page', { page_name: pageName, title });
};

export const trackManualFormatting = (type: string) => {
  console.log('[GA4] manual_formatting:', { type });
  ReactGA.event('manual_formatting', { type });
  amplitude.track('manual_formatting', { type });
};

export const trackImageInserted = () => {
  console.log('[GA4] image_inserted');
  ReactGA.event('image_inserted', {});
  amplitude.track('image_inserted', {});
};

export const trackAutosave = (timestamp: string) => {
  console.log('[GA4] autosave:', { timestamp });
  ReactGA.event('autosave', { timestamp });
  amplitude.track('autosave', { timestamp });
};

export const trackAutosaveFailed = (errorCode: string) => {
  console.log('[GA4] autosave_failed:', { error_code: errorCode });
  ReactGA.event('autosave_failed', { error_code: errorCode });
  amplitude.track('autosave_failed', { error_code: errorCode });
};

export const trackEditorCrash = () => {
  console.log('[GA4] editor_crash');
  ReactGA.event('editor_crash', {});
  amplitude.track('editor_crash', {});
};

export const trackEditorAbandoned = () => {
  console.log('[GA4] editor_abandoned');
  ReactGA.event('editor_abandoned', {});
  amplitude.track('editor_abandoned', {});
};

// ==================== PREVIEW EVENTS ====================

export const trackPreviewClicked = (bookTitle: string) => {
  console.log('[GA4] clicked (preview):', { book_title: bookTitle });
  ReactGA.event('clicked', { book_title: bookTitle });
  amplitude.track('clicked', { book_title: bookTitle });
};

export const trackPreviewAbandoned = () => {
  console.log('[GA4] preview_abandoned');
  ReactGA.event('preview_abandoned', {});
  amplitude.track('preview_abandoned', {});
};

// isFirstRealContent: true only when this book had no uploaded file AND no
// real typed content before this specific generation — someone clicking
// "Generate Preview" for the fifth time on the same book shouldn't refire Lead.
export const trackPdfGenerationComplete = (pageCount: number, generationTimeMs: number, isFirstRealContent: boolean) => {
  console.log('[GA4] pdf_generation_complete:', { page_count: pageCount, generation_time_ms: generationTimeMs });
  ReactGA.event('pdf_generation_complete', { page_count: pageCount, generation_time_ms: generationTimeMs });
  amplitude.track('pdf_generation_complete', { page_count: pageCount, generation_time_ms: generationTimeMs });
  // The typed-in-editor equivalent of a manuscript upload — the author wrote
  // enough real content to want to see it rendered as a book. Covers both the
  // primary and legacy preview-generation code paths (same call, either branch).
  // Only the first time for this book.
  if (isFirstRealContent) trackMetaPixel('Lead');
};

export const trackPdfGenerationFailed = (errorReason: string, params?: {
  book_id?: string;
  book_title?: string;
  section_count?: number;
  generator?: 'backend' | 'legacy';
}) => {
  const payload = { error_reason: errorReason, ...params };
  console.log('[GA4] pdf_generation_failed:', payload);
  ReactGA.event('pdf_generation_failed', payload);
  amplitude.track('pdf_generation_failed', payload);
};

export const trackPdfDownloaded = () => {
  console.log('[GA4] pdf_downloaded');
  ReactGA.event('pdf_downloaded', {});
  amplitude.track('pdf_downloaded', {});
};

// ==================== DOCX IMPORT EVENTS ====================

export const trackDocxImportInitiated = (fileSizeKb: number) => {
  console.log('[GA4] docx_import_initiated:', { file_size_kb: fileSizeKb });
  ReactGA.event('docx_import_initiated', { file_size_kb: fileSizeKb });
  amplitude.track('docx_import_initiated', { file_size_kb: fileSizeKb });
};

export const trackDocxImportParseFailed = (errorMessage?: string) => {
  console.log('[GA4] docx_import_parse_failed:', { error_message: errorMessage });
  ReactGA.event('docx_import_parse_failed', { error_message: errorMessage ?? 'unknown' });
  amplitude.track('docx_import_parse_failed', { error_message: errorMessage ?? 'unknown' });
};

export const trackDocxImportPreviewShown = (params: {
  sections_count: number;
  chapters: number;
  front_matter: number;
  end_matter: number;
  toc_removed: number;
  images_count: number;
}) => {
  console.log('[GA4] docx_import_preview_shown:', params);
  ReactGA.event('docx_import_preview_shown', params);
  amplitude.track('docx_import_preview_shown', params);
};

export const trackDocxSectionMerged = () => {
  console.log('[GA4] docx_section_merged');
  ReactGA.event('docx_section_merged', {});
  amplitude.track('docx_section_merged', {});
};

export const trackDocxSectionRenamed = () => {
  console.log('[GA4] docx_section_renamed');
  ReactGA.event('docx_section_renamed', {});
  amplitude.track('docx_section_renamed', {});
};

export const trackDocxSectionTypeChanged = (from: string, to: string) => {
  console.log('[GA4] docx_section_type_changed:', { from, to });
  ReactGA.event('docx_section_type_changed', { from, to });
  amplitude.track('docx_section_type_changed', { from, to });
};

export const trackDocxSectionsReordered = () => {
  console.log('[GA4] docx_sections_reordered');
  ReactGA.event('docx_sections_reordered', {});
  amplitude.track('docx_sections_reordered', {});
};

export const trackDocxImportUndoUsed = () => {
  console.log('[GA4] docx_import_undo_used');
  ReactGA.event('docx_import_undo_used', {});
  amplitude.track('docx_import_undo_used', {});
};

export const trackDocxImportCancelled = (params: { sections_count: number; time_in_modal_ms: number }) => {
  console.log('[GA4] docx_import_cancelled:', params);
  ReactGA.event('docx_import_cancelled', params);
  amplitude.track('docx_import_cancelled', params);
};

export const trackDocxImportConfirmed = (params: { sections_count: number; time_in_modal_ms: number }) => {
  console.log('[GA4] docx_import_confirmed:', params);
  ReactGA.event('docx_import_confirmed', params);
  amplitude.track('docx_import_confirmed', params);
};

// isFirstRealContent: true only when this book had no uploaded file AND no
// real typed content before this specific import — a re-import (see
// trackDocxReimportInitiated) replaces existing content, it isn't a new Lead.
export const trackDocxImportCompleted = (params: {
  duration_ms: number;
  images_uploaded: number;
  book_language: string;
  isFirstRealContent: boolean;
}) => {
  console.log('[GA4] docx_import_completed:', params);
  ReactGA.event('docx_import_completed', params);
  amplitude.track('docx_import_completed', params);
  // Third journey for bringing in a manuscript, alongside upload (trackUploadComplete)
  // and typed content (trackPdfGenerationComplete) — same "real content landed"
  // bar, so all three get equivalent Lead treatment. Only the first time for this book.
  if (params.isFirstRealContent) trackMetaPixel('Lead');
};

export const trackDocxImportFailed = (errorMessage?: string) => {
  console.log('[GA4] docx_import_failed:', { error_message: errorMessage });
  ReactGA.event('docx_import_failed', { error_message: errorMessage ?? 'unknown' });
  amplitude.track('docx_import_failed', { error_message: errorMessage ?? 'unknown' });
};

export const trackDocxReimportInitiated = () => {
  console.log('[GA4] docx_reimport_initiated');
  ReactGA.event('docx_reimport_initiated', {});
  amplitude.track('docx_reimport_initiated', {});
};

// ==================== BOOK LIFECYCLE EVENTS ====================

export const trackBookCreated = (bookId: string, title: string) => {
  console.log('[GA4] book_created:', { book_id: bookId, title });
  ReactGA.event('book_created', { book_id: bookId, title });
  amplitude.track('book_created', { book_id: bookId, title });
};

export const trackBookSubmitted = (bookId: string, title: string, extra?: Record<string, any>) => {
  const payload = { book_id: bookId, title, ...extra };
  console.log('[GA4] book_submitted:', payload);
  ReactGA.event('book_submitted', payload);
  amplitude.track('book_submitted', payload);
  // extra.total_price comes from PricingStep's handleSubmitForReview — the
  // author's final priced order total at the moment they submit.
  trackMetaPixel('InitiateCheckout', { value: extra?.total_price, currency: 'INR' });
};

// ==================== BOOK COVER EVENTS ====================

export const trackCoverImageUploaded = (fileSizeMb: number) => {
  console.log('[GA4] cover_image_uploaded:', { file_size_mb: fileSizeMb });
  ReactGA.event('cover_image_uploaded', { file_size_mb: fileSizeMb });
  amplitude.track('cover_image_uploaded', { file_size_mb: fileSizeMb });
};

export const trackCoverImageUploadFailed = (errorReason: string) => {
  console.log('[GA4] cover_image_upload_failed:', { error_reason: errorReason });
  ReactGA.event('cover_image_upload_failed', { error_reason: errorReason });
  amplitude.track('cover_image_upload_failed', { error_reason: errorReason });
};

export const trackCoverImageRemoved = () => {
  console.log('[GA4] cover_image_removed');
  ReactGA.event('cover_image_removed', {});
  amplitude.track('cover_image_removed', {});
};

// Keep in sync with COVER_DESIGN_PRICE in PricingStep.tsx — duplicated here
// rather than threaded through every call site, since it's a fixed price,
// not something callers compute.
const COVER_DESIGN_ADDON_PRICE_INR = 2500;

export const trackCoverDesignAddonAdded = (designType: string | null) => {
  console.log('[GA4] cover_design_addon_added:', { design_type: designType });
  ReactGA.event('cover_design_addon_added', { design_type: designType });
  amplitude.track('cover_design_addon_added', { design_type: designType });
  trackMetaPixel('AddToCart', {
    content_name: designType,
    value: COVER_DESIGN_ADDON_PRICE_INR,
    currency: 'INR',
  });
};

export const trackCoverDesignAddonRemoved = () => {
  console.log('[GA4] cover_design_addon_removed');
  ReactGA.event('cover_design_addon_removed', {});
  amplitude.track('cover_design_addon_removed', {});
};

export const trackCoverSwitchAlternative = (bindingType: string, paperType: string) => {
  console.log('[GA4] cover_switch_alternative:', { binding_type: bindingType, paper_type: paperType });
  ReactGA.event('cover_switch_alternative', { binding_type: bindingType, paper_type: paperType });
  amplitude.track('cover_switch_alternative', { binding_type: bindingType, paper_type: paperType });
};

export const trackCoverDesignerOpened = () => {
  console.log('[GA4] cover_designer_opened');
  ReactGA.event('cover_designer_opened', {});
  amplitude.track('cover_designer_opened', {});
};

export const trackCoverStepCompleted = () => {
  console.log('[GA4] cover_step_completed');
  ReactGA.event('cover_step_completed', {});
  amplitude.track('cover_step_completed', {});
};

// ==================== PRICING EVENTS ====================

export const trackPricingStepLanded = (params: {
  total_price: number;
  spellcheck_added: boolean;
  cover_design_added: boolean;
  has_cover_image: boolean;
}) => {
  console.log('[GA4] pricing_step_landed:', params);
  ReactGA.event('pricing_step_landed', params);
  amplitude.track('pricing_step_landed', params);
};

export const trackAddonRemoved = (addonType: 'spellcheck' | 'coverDesign') => {
  console.log('[GA4] addon_removed:', { addon_type: addonType });
  ReactGA.event('addon_removed', { addon_type: addonType });
  amplitude.track('addon_removed', { addon_type: addonType });
};

export const trackPricingSwitchAlternative = (bindingType: string, paperType: string) => {
  console.log('[GA4] pricing_switch_alternative:', { binding_type: bindingType, paper_type: paperType });
  ReactGA.event('pricing_switch_alternative', { binding_type: bindingType, paper_type: paperType });
  amplitude.track('pricing_switch_alternative', { binding_type: bindingType, paper_type: paperType });
};

// ==================== ADDITIONAL ERROR EVENTS ====================

export const trackApprovePageCountFailed = (errorReason: string) => {
  console.log('[GA4] approve_page_count_failed:', { error_reason: errorReason });
  ReactGA.event('approve_page_count_failed', { error_reason: errorReason });
  amplitude.track('approve_page_count_failed', { error_reason: errorReason });
};

export const trackSaveAndContinueFailed = (errorReason: string) => {
  console.log('[GA4] save_and_continue_failed:', { error_reason: errorReason });
  ReactGA.event('save_and_continue_failed', { error_reason: errorReason });
  amplitude.track('save_and_continue_failed', { error_reason: errorReason });
};

export const trackBookLoadFailed = (errorReason: string) => {
  console.log('[GA4] book_load_failed:', { error_reason: errorReason });
  ReactGA.event('book_load_failed', { error_reason: errorReason });
  amplitude.track('book_load_failed', { error_reason: errorReason });
};

export const trackSpellcheckFailed = (errorReason: string) => {
  console.log('[GA4] spellcheck_failed:', { error_reason: errorReason });
  ReactGA.event('spellcheck_failed', { error_reason: errorReason });
  amplitude.track('spellcheck_failed', { error_reason: errorReason });
};

export const trackEditorImageUploadFailed = (errorReason: string) => {
  console.log('[GA4] editor_image_upload_failed:', { error_reason: errorReason });
  ReactGA.event('editor_image_upload_failed', { error_reason: errorReason });
  amplitude.track('editor_image_upload_failed', { error_reason: errorReason });
};

export const trackImageUrlConversionFailed = (errorReason: string) => {
  console.log('[GA4] image_url_conversion_failed:', { error_reason: errorReason });
  ReactGA.event('image_url_conversion_failed', { error_reason: errorReason });
  amplitude.track('image_url_conversion_failed', { error_reason: errorReason });
};

export const trackCoverImageRemovalFailed = (errorReason: string) => {
  console.log('[GA4] cover_image_removal_failed:', { error_reason: errorReason });
  ReactGA.event('cover_image_removal_failed', { error_reason: errorReason });
  amplitude.track('cover_image_removal_failed', { error_reason: errorReason });
};

export const trackFileDownloadFailed = (errorReason: string) => {
  console.log('[GA4] file_download_failed:', { error_reason: errorReason });
  ReactGA.event('file_download_failed', { error_reason: errorReason });
  amplitude.track('file_download_failed', { error_reason: errorReason });
};
